import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  FormControlLabel,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Switch, { SwitchProps } from "@mui/material/Switch";
import { useDispatch } from "react-redux";
import { setTitle } from "store/reducer/appReducer";
import twoFactorService from "services/admin/twoFactor.service";
import type { TwoFactorSettings, TwoFactorUpdateRequest } from "interfaces/admin/admin.interface";

// Styled Switch Component
const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 50,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(24px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "#65C466",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.mode === "light" ? "#000" : "#fff",
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

export default function TwoFactorSettingsPage() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<TwoFactorSettings>({
    twoFactorEnabled: false,
    phoneNumber: ""
  });
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error" | "info" | "warning"; text: string } | null>(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await twoFactorService.getMy2FASettings();
      if (response.data) {
        setSettings(response.data);
        setPhoneNumber(convertToLocalFormat(response.data.phoneNumber) || "");
      }
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch(setTitle("การตั้งค่า Two-Factor Authentication"));
    loadSettings();
  }, [dispatch, loadSettings]);

  const handleToggle2FA = (enabled: boolean) => {
    setSettings(prev => ({ ...prev, twoFactorEnabled: enabled }));
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value);
  };

    const formatPhoneNumber = (phone: string): string => {
    if (!phone) return phone;
    const digits = phone.replace(/\D/g, "");
    
    // Convert 0xxxxxxxxx format to +66xxxxxxxxx
    if (digits.startsWith("0") && digits.length === 10) {
      return "+66" + digits.substring(1);
    }
    
    // If already has +66 prefix, return as is
    if (digits.startsWith("66") && digits.length === 11) {
      return "+" + digits;
    }
    
    // For 9-digit numbers, assume it's missing the leading 0
    if (digits.length === 9 && !digits.startsWith("0") && !digits.startsWith("66")) {
      return "+66" + digits;
    }
    
    return phone;
  };

  const convertToLocalFormat = (phone: string): string => {
    if (!phone) return phone;
    const digits = phone.replace(/\D/g, "");
    
    // Convert +66xxxxxxxxx back to 0xxxxxxxxx for input display
    if (digits.startsWith("66") && digits.length === 11) {
      return "0" + digits.substring(2);
    }
    
    return phone;
  };

  const formatPhoneNumberForDisplay = (phone: string): string => {
    if (!phone) return phone;
    const digits = phone.replace(/\D/g, "");
    
    // Convert +66xxxxxxxxx format to 0xx-xxx-xxxx for display
    if (digits.startsWith("66") && digits.length === 11) {
      const localNumber = "0" + digits.substring(2);
      if (localNumber.length === 10) {
        return `${localNumber.substring(0, 3)}-${localNumber.substring(3, 6)}-${localNumber.substring(6)}`;
      }
      return localNumber;
    }
    
    // Format 0xxxxxxxxx to 0xx-xxx-xxxx
    if (digits.startsWith("0") && digits.length === 10) {
      return `${digits.substring(0, 3)}-${digits.substring(3, 6)}-${digits.substring(6)}`;
    }
    
    return phone;
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^\+66[0-9]{9}$/;
    return phoneRegex.test(phone);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      // Check if enabling 2FA and no phone number exists
      if (settings.twoFactorEnabled && !phoneNumber && !settings.phoneNumber) {
        setMessage({
          type: "error",
          text: "กรุณากรอกหมายเลขโทรศัพท์ในข้อมูลส่วนตัวก่อนเปิดใช้งาน 2FA หรือกรอกหมายเลขโทรศัพท์ใหม่"
        });
        return;
      }

      if (settings.twoFactorEnabled) {
        let phoneToUse = phoneNumber || settings.phoneNumber;
        
        // Only validate and format if user entered a new phone number
        if (phoneNumber && phoneNumber !== settings.phoneNumber) {
          const formattedPhone = formatPhoneNumber(phoneNumber);
          if (!validatePhoneNumber(formattedPhone)) {
            setMessage({
              type: "error",
              text: "กรุณากรอกหมายเลขโทรศัพท์ที่ถูกต้อง (เช่น 0812345678 หรือ +66812345678)"
            });
            return;
          }
          phoneToUse = formattedPhone;
        }
        
        const updateRequest: TwoFactorUpdateRequest = {
          twoFactorEnabled: settings.twoFactorEnabled,
          phoneNumber: phoneToUse !== settings.phoneNumber ? phoneToUse : undefined
        };

        const response = await twoFactorService.updateMy2FASettings(updateRequest);
        setSettings(response.data);
        setPhoneNumber(convertToLocalFormat(response.data.phoneNumber) || "");
      } else {
        // Disable 2FA
        const updateRequest: TwoFactorUpdateRequest = {
          twoFactorEnabled: false
        };

        const response = await twoFactorService.updateMy2FASettings(updateRequest);
        setSettings(response.data);
        setMessage({
          type: "warning",
          text: "ปิดใช้งาน Two-Factor Authentication เรียบร้อยแล้ว"
        });
        return; // Return early to avoid the duplicate message setting below
      }

      setMessage({
        type: "success",
        text: "เปิดใช้งาน Two-Factor Authentication เรียบร้อยแล้ว"
      });
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "เกิดข้อผิดพลาดในการบันทึกข้อมูล"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              การตั้งค่า Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Two-Factor Authentication เป็นการเพิ่มความปลอดภัยให้กับบัญชีของคุณ 
              โดยจะส่ง OTP ไปยังหมายเลขโทรศัพท์ของคุณเมื่อเข้าสู่ระบบ
            </Typography>

            {message && (
              <Alert 
                severity={message.type} 
                sx={{ mb: 2 }}
                onClose={() => setMessage(null)}
              >
                {message.text}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={settings.twoFactorEnabled}
                      onChange={(e) => handleToggle2FA(e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                        เปิดใช้งาน Two-Factor Authentication
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {settings.twoFactorEnabled 
                          ? "2FA เปิดใช้งานอยู่ - ระบบจะส่ง OTP ไปยังโทรศัพท์ของคุณ"
                          : "2FA ปิดใช้งานอยู่ - เข้าสู่ระบบด้วยรหัสผ่านเท่านั้น"
                        }
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>

              {settings.twoFactorEnabled && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      หมายเลขโทรศัพท์ปัจจุบัน: <strong>{settings.phoneNumber ? formatPhoneNumberForDisplay(settings.phoneNumber) : "ยังไม่ได้กรอก"}</strong>
                    </Typography>
                    {!settings.phoneNumber && (
                      <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                        ⚠️ กรุณากรอกหมายเลขโทรศัพท์ในข้อมูลส่วนตัวก่อน หรือกรอกหมายเลขใหม่ด้านล่าง
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="หมายเลขโทรศัพท์สำหรับ 2FA"
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      placeholder={settings.phoneNumber || "0812345678 หรือ +66812345678"}
                      helperText={
                        settings.phoneNumber 
                          ? "ใช้เบอร์เดียวกับในโปรไฟล์ หรือกรอกเบอร์ใหม่เพื่อเปลี่ยนเบอร์ในโปรไฟล์ด้วย"
                          : "กรุณากรอกหมายเลขโทรศัพท์ที่ใช้รับ SMS OTP"
                      }
                      required={!settings.phoneNumber}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : null}
                  >
                    {saving ? "กำลังบันทึก..." : "บันทึกการตั้งค่า"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
